import { type TranscriptEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';

function isRunBoundary(entry: TranscriptEntry): boolean {
    return entry.kind === 'activity' && entry.lifecycleKind !== undefined;
}

function isRunStart(entry: TranscriptEntry): boolean {
    return entry.kind === 'activity' && entry.lifecycleKind === 'run-started';
}

function isNativeWakeEvent(entry: TranscriptEntry): boolean {
    return (
        entry.kind === 'activity' &&
        (entry.sourceEventType === 'poke' || entry.sourceEventType === 'watch')
    );
}

/**
 * Removes transcript data that belongs only to the native renderer before the
 * legacy timeline sees it. Empty run boundaries attached to that data leave
 * with it, while debug rows and runs with real legacy content stay intact.
 */
export function adaptTranscriptForLegacy(
    entries: ReadonlyArray<TranscriptEntry>,
): Array<TranscriptEntry> {
    const adapted: Array<TranscriptEntry> = [];
    let pendingEmptyRun: {
        readonly entries: Array<TranscriptEntry>;
        containsNativeWakeEvent: boolean;
    } | null = null;

    const flushPendingRun = () => {
        if (pendingEmptyRun !== null) {
            const run = pendingEmptyRun;
            adapted.push(
                ...run.entries.filter(
                    (entry) =>
                        !run.containsNativeWakeEvent ||
                        entry.kind === 'debug-event',
                ),
            );
            pendingEmptyRun = null;
        }
    };

    for (const entry of entries) {
        if (isRunStart(entry)) {
            flushPendingRun();
            pendingEmptyRun = {
                entries: [entry],
                containsNativeWakeEvent: false,
            };
            continue;
        }

        if (isNativeWakeEvent(entry)) {
            if (pendingEmptyRun !== null) {
                pendingEmptyRun.containsNativeWakeEvent = true;
            }
            continue;
        }

        if (
            pendingEmptyRun !== null &&
            (isRunBoundary(entry) || entry.kind === 'debug-event')
        ) {
            pendingEmptyRun.entries.push(entry);
            continue;
        }

        // A native wake event can be followed by ordinary assistant output.
        // Once real legacy content arrives, keep the run boundaries so its
        // duration and status remain visible in the V2 timeline.
        if (pendingEmptyRun !== null) {
            pendingEmptyRun.containsNativeWakeEvent = false;
        }
        flushPendingRun();
        adapted.push(entry);
    }

    flushPendingRun();
    return adapted;
}
